import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useQuery } from 'react-apollo';
import { toast } from 'react-toastify';
import ReactSVG from 'react-svg';
import { disableBodyScroll } from 'body-scroll-lock';
import ReactGA from 'react-ga';
import default_listing_image from 'default_listing_image.svg';
import heart_path from 'heart_path.svg';
import Pill from './Pill';
import Ratings from './Ratings';
import FavoriteSuccessBanner from './CustomToast/FavoriteSuccessBanner';
import { handleHeartAnimation } from '../utils/handleHeartAnimation';
import { userQuery } from '../constants/queries';
import DealTag from './DealTag';
import ErrorBoundary from './ErrorBoundary';
import { clientNoVarnish } from './client';

function ListingCard({ listing, is_small, setHoveredItem, id }) {
  // fallbacks for medium, small, then default image
  const displayImage =
    listing.small_featured_image_url ||
    listing.medium_featured_image_url ||
    default_listing_image;

  // Initialize ReactGA on page load
  useEffect(() => {
    ReactGA.initialize('UA-134642264-1');
  }, []);

  const { data: userData } = useQuery(userQuery, {
    fetchPolicy: 'no-cache',
    client: clientNoVarnish,
  });
  const heartRef = useRef(null);
  const cardRef = useRef(null);

  const [isFavorited, setFavorited] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line
    listing.favorited && setFavorited(true);
  }, [listing.favorited]);

  const [favoriteRoute, setFavoriteRoute] = useState(null);
  useEffect(() => {
    setFavoriteRoute(window.Routes.favorite_listing_path({ id: listing.slug }));
  }, [listing.slug]);

  const handleFavorite = () => {
    const tokenElement = document.querySelector('[name=csrf-token]');
    if (userData && Object.keys(userData.user).length > 0) {
      handleHeartAnimation({ ref: heartRef, isFavorited });
      axios
        .patch(favoriteRoute, null, {
          headers: {
            'X-CSRF-Token': tokenElement ? tokenElement.content : '',
            Accept: 'application/json',
          },
        })
        .then(res => {
          const { pathname } = window.location;
          if (res.status === 200) {
            toast.dismiss();
            if (!isFavorited)
              toast(<FavoriteSuccessBanner favorited type={listing} />);
            else toast(<FavoriteSuccessBanner type={listing} />);
            setFavorited(!isFavorited);

            if (
              pathname.includes('/account') ||
              pathname.includes('/favorites')
            ) {
              cardRef.current.style.opacity = 0;
              setTimeout(() => {
                cardRef.current.parentElement.remove();
              }, 300);
            }
          }
        })
        .catch(err => {
          handleHeartAnimation({ ref: heartRef, isFavorited: !isFavorited });
          console.error(err);
          if (err.response.status === 401) {
            window.location.pathname = window.Routes.login_path();
          }
        });
    } else {
      const modal = document.querySelector('#popup-modal');
      modal.classList.remove('fadeOut');
      modal.classList.remove('hidden');
      modal.classList.add('fadeIn');
      disableBodyScroll(modal);
    }
  };

  if (!document.querySelector('[name=csrf-token]').content) {
    console.warning('Could not find csrf-token');
  }

  const imageError = e => {
    e.target.src = default_listing_image;
  };

  // Sometimes the featured_image_url will be the default image. Don't display default image as featured image
  const noListingImage =
    (!listing.medium_featured_image_url ||
      listing.medium_featured_image_url.includes('default_listing_image')) &&
    (!listing.small_featured_image_url ||
      listing.small_featured_image_url.includes('default_listing_image'));

  const displayImageSplit = displayImage.split('.');
  const endFormat = displayImageSplit[displayImageSplit.length - 1];
  let imageFormatType = 'jpeg';
  if (endFormat === 'png') imageFormatType = 'png';
  if (endFormat === 'svg') imageFormatType = 'svg+xml';

  let pillText = '';
  if (listing.is_premier) pillText = 'Premier';
  if (listing.affiliate_link) pillText = 'Book Online';

  return (
    <div
      id={id}
      ref={cardRef}
      className={`listing-card ${is_small ? 'is_small' : ''}`}
      onMouseOver={() => {
        if (setHoveredItem) setHoveredItem(listing);
      }}
      onMouseLeave={() => {
        if (setHoveredItem) setHoveredItem(null);
      }}
      onFocus={() => {
        if (setHoveredItem) setHoveredItem(listing);
      }}
      onBlur={() => {
        if (setHoveredItem) setHoveredItem(null);
      }}
    >
      <section className="pill--container listing__pill--container">
        {pillText && (
          <Pill isRedPill={!!listing.affiliate_link}>{pillText}</Pill>
        )}
      </section>
      <button
        aria-label="Favorite Listing"
        name="Favorite Listing"
        ref={heartRef}
        type="button"
        className={`card-heart ${listing.favorited ? 'favorited' : ''}`}
        onClick={handleFavorite}
      >
        <ReactSVG src={heart_path} className="heart-icon" />
      </button>
      {/* !!! SOME LISTINGS DON'T HAVE CITY ATTACHED !!! */}
      <a
        aria-label="View Listing"
        href={window.Routes.listing_type_show_path({
          area: listing.city_name || '30a',
          is_type: listing.is_type,
          category: listing.category ? listing.category.slug : 'fake',
          url: listing.slug,
        })}
        onClick={() => {
          if (window.location.pathname === '/search') {
            ReactGA.event({
              category: 'Listing',
              action: 'Viewed Listing from Search',
            });
          }
        }}
      >
        <section
          className={`featured-image-container ${
            noListingImage ? 'no_image' : ''
          }`}
        >
          <div className="featured-image-overlay" />
          <picture className="background-picture">
            <source
              data-srcset={`${listing.medium_featured_image_url} 606w, ${listing.small_featured_image_url} 337w`}
              type={`image/${imageFormatType}`}
            />
            <img
              className="listing__featured-image lazyload"
              data-src={displayImage}
              alt={listing.title}
              title={`${listing.title} Image`}
              onError={imageError}
            />
          </picture>
        </section>

        <section className="listing-info">
          <div className="listing-icon-container">
            <ReactSVG
              src={listing.category_image_url}
              className="listing-icon"
            />

            <p className="secondary-text">
              {listing.category ? listing.category.name : ''}
            </p>
          </div>
          <section className="listing-rating-deal">
            {listing.special_listing && <DealTag />}
            <Ratings
              rating={listing.rating}
              rating_count={listing.rating_count}
            />
          </section>
          <h3 className="listing-title">{listing.title}</h3>
        </section>
      </a>
    </div>
  );
}

ListingCard.propTypes = {
  listing: PropTypes.object.isRequired,
  is_small: PropTypes.bool,
  setHoveredItem: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default function ListingCardWrapper({ ...props }) {
  return (
    <ErrorBoundary>
      <ListingCard {...props} />
    </ErrorBoundary>
  );
}
